import type {
  MerchandiseMovement,
  MerchandiseMovementFormData,
  MerchandiseMovementStats,
  MerchandiseMovementFilters
} from '@/types/merchandise-movement.types';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

export class MerchandiseMovementService {
  private static getAuthToken(): string {
    return localStorage.getItem('accessToken') || '';
  }

  private static async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    try {
      const token = this.getAuthToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        ...options?.headers,
      };

      if (token) {
        (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        headers,
        ...options,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API Request Error:', error);
      throw error;
    }
  }

  static async getMerchandiseMovements(filters: MerchandiseMovementFilters = {}): Promise<MerchandiseMovement[]> {
    const searchParams = new URLSearchParams();

    // Convert filters to search parameters
    if (filters.direction && filters.direction !== 'all') {
      searchParams.append('direction', filters.direction);
    }
    if (filters.status && filters.status !== 'all') {
      searchParams.append('status', filters.status);
    }
    if (filters.locationId && filters.locationId !== 'all') {
      searchParams.append('locationId', filters.locationId);
    }
    if (filters.movementType && filters.movementType !== 'all') {
      searchParams.append('movementType', filters.movementType);
    }
    if (filters.dateFrom) {
      searchParams.append('dateFrom', filters.dateFrom);
    }
    if (filters.dateTo) {
      searchParams.append('dateTo', filters.dateTo);
    }
    if (filters.requestedBy) {
      searchParams.append('requestedBy', filters.requestedBy);
    }
    if (filters.ingredientId && filters.ingredientId !== 'all') {
      searchParams.append('search', filters.ingredientId);
    }

    const queryString = searchParams.toString();
    const endpoint = `/merchandise-movements${queryString ? `?${queryString}` : ''}`;

    const response = await this.request<{ data: MerchandiseMovement[] }>(endpoint);
    return response.data;
  }

  static async getMerchandiseMovementStats(): Promise<MerchandiseMovementStats> {
    const apiResponse = await this.request<{
      totalMovements: number;
      totalEntries: number;
      totalExits: number;
      pendingApproval: number;
      totalValueEntries?: number;
      totalValueExits?: number;
      netValue?: number;
    }>('/merchandise-movements/stats');

    // Get approved/rejected counts from movements list
    const movements = await this.getMerchandiseMovements();
    const approved = movements.filter(m => m.status === 'approved').length;
    const rejected = movements.filter(m => m.status === 'rejected').length;

    // Transform API response to match frontend expectations
    return {
      total: apiResponse.totalMovements,
      totalEntries: apiResponse.totalEntries,
      totalExits: apiResponse.totalExits,
      pendingApproval: apiResponse.pendingApproval,
      approved,
      rejected
    };
  }

  static async createMerchandiseMovement(movementData: MerchandiseMovementFormData): Promise<MerchandiseMovement> {
    const response = await this.request<{ data: MerchandiseMovement }>('/merchandise-movements', {
      method: 'POST',
      body: JSON.stringify(movementData),
    });
    return response.data;
  }

  static async approveMerchandiseMovement(movementId: string): Promise<MerchandiseMovement> {
    const response = await this.request<{ data: MerchandiseMovement }>(`/merchandise-movements/${movementId}/approve`, {
      method: 'PUT',
    });
    return response.data;
  }

  static async rejectMerchandiseMovement(movementId: string, rejectionReason: string): Promise<MerchandiseMovement> {
    const response = await this.request<{ data: MerchandiseMovement }>(`/merchandise-movements/${movementId}/reject`, {
      method: 'PUT',
      body: JSON.stringify({ rejectionReason }),
    });
    return response.data;
  }

  static async cancelMerchandiseMovement(movementId: string, cancellationReason: string): Promise<MerchandiseMovement> {
    const response = await this.request<{ data: MerchandiseMovement }>(`/merchandise-movements/${movementId}/cancel`, {
      method: 'PUT',
      body: JSON.stringify({ reason: cancellationReason }),
    });
    return response.data;
  }

  static async getMerchandiseMovement(movementId: string): Promise<MerchandiseMovement> {
    const response = await this.request<{ data: MerchandiseMovement }>(`/merchandise-movements/${movementId}`);
    return response.data;
  }
}